import React, { useState } from 'react';
import { Modal } from '../common/Modal';
import { FileUploadZone } from './upload/FileUploadZone';
import { ValidationErrors } from './upload/ValidationErrors';
import { WarningMessage } from './upload/WarningMessage';
import { Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { generatePackageTemplate } from '../../utils/packageValidation';
import { useLanguage } from '../../contexts/LanguageContext';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (data: any[]) => Promise<{ message: string }>;
};

export const UpdatePackagesModal = ({ isOpen, onClose, onUpdate }: Props) => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const { translations } = useLanguage();

  const handleDownloadTemplate = () => {
    const template = generatePackageTemplate();
    const ws = XLSX.utils.json_to_sheet([template]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "packages-template.xlsx");
  };

  const handleUpload = async () => {
    if (!file) {
      setValidationErrors([translations?.updatePackagesModal?.fileRequired || 'Please select a file to upload']);
      return;
    }

    setIsUploading(true);
    setValidationErrors([]);

    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          setValidationErrors([translations?.updatePackagesModal?.emptyFile || 'The uploaded file is empty']);
          setIsUploading(false);
          return;
        }

        try {
          const result = await onUpdate(jsonData);
          onClose();
        } catch (error: any) {
          console.error('Error updating packages:', error);
          setValidationErrors([error.message]);
        }
      } catch (error: any) {
        console.error('Error parsing Excel file:', error);
        setValidationErrors([translations?.updatePackagesModal?.parseError || 'Failed to parse Excel file']);
      } finally {
        setIsUploading(false);
      }
    };

    reader.onerror = () => {
      setValidationErrors([translations?.updatePackagesModal?.readError || 'Failed to read the file']);
      setIsUploading(false);
    };

    reader.readAsBinaryString(file);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={translations?.updatePackagesModal?.title || 'Update Packages'}
    >
      <div className="space-y-6">
        <button
          onClick={handleDownloadTemplate}
          className="flex items-center gap-2 text-primary-600 hover:text-primary-700"
        >
          <Download size={20} />
          <span>{translations?.updatePackagesModal?.downloadTemplate || 'Download Template'}</span>
        </button>

        <FileUploadZone
          file={file}
          onFileSelect={setFile}
          onFileRemove={() => {
            setFile(null);
            setValidationErrors([]);
          }}
        />

        <ValidationErrors errors={validationErrors} />
        <WarningMessage />

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            {translations?.updatePackagesModal?.cancel || 'Cancel'}
          </button>
          <button
            onClick={handleUpload}
            disabled={isUploading || !file}
            className={`px-6 py-2 rounded-lg text-white
              ${isUploading || !file ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary-500 hover:bg-primary-600'}`}
          >
            {isUploading
              ? translations?.updatePackagesModal?.updating || 'Updating...'
              : translations?.updatePackagesModal?.update || 'Update'}
          </button>
        </div>
      </div>
    </Modal>
  );
};